import { SearchLoop, Filter } from 'assets/icons';
import StyledInput from 'components/styledInput/StyledInput';
import ThreeStateCheckbox from 'components/threeStateCheckbox/threeStateCheckbox';
import Fuse from 'fuse.js';
import UseContext from 'hooks/useContext';
import useImportedTimeSheet from 'hooks/useImportedTimeSheet';
import MediaQuery from 'react-responsive';
import { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import removeAccents from 'remove-accents';
import ApiDatabase from 'server';
import PageForm from '../../components/pageForm/PageForm';
import SubNavbar from '../../components/SubNavbar/SubNavbar';
import TimeSheetSubNav from '../../components/subnav/timeSheet.subNav';
import Button from '../../components/button';
import BottomSheet from '../../components/bottomSheet/BottomSheet';
import { formatDateToYMD } from '../../helper/helper';

const SEARCH_TYPE_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'id_time_sheet', label: 'ID Relevé Heure' },
  { value: 'id_mission', label: 'ID Mission' },
  { value: 'id_contract_bestt', label: 'ID BeSTT Contrat' },
  { value: 'id_bestt_temp_worker', label: 'ID BeSTT Intérimaire' },
  { value: 'name_company', label: 'Nom Entreprise' },
  { value: 'temp_worker_name', label: 'Nom Intérimaire' },
];

const FUSE_OPTIONS = {
  keys: [
    'id_time_sheet', 'id_user', 'title', 'id_mission_reel',
    'lastname', 'firstname', 'id_bestt_temp_worker',
    'reason_social', 'name_commercial', 'id_bestt_company',
    'id_bestt_contract', 'id_mission',
  ],
  includeScore: true,
  threshold: 0.3,
  distance: 100,
  shouldSort: true,
  getFn: (obj, path) => {
    const value = Fuse.config.getFn(obj, path);
    return value == null ? '' : removeAccents(value.toString().toLowerCase());
  },
};

const SearchTypeSelect = ({ value, onChange, className = '' }) => (
  <select
    className={`w-full h-10 pl-2 pr-3 ft-b placeholder-gray-600 border rounded-md text-sm focus:border-yellow-500 focus:outline-none ${className}`}
    onChange={(e) => onChange(e.target.value)}
    value={value}
  >
    {SEARCH_TYPE_OPTIONS.map((opt) => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

const TimeSheet = () => {
  const location = useLocation();
  const { companyData, refreshTime } = UseContext();

  const [arrayTimeSheets, setArrayTimeSheets] = useState([]);
  const [isWaiting, setIsWaiting] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limitElement] = useState(20);
  const [countPerPage, setCountPerPage] = useState(0);
  const [timeSheetSearch, setTimeSheetSearch] = useState('');
  const [timeSheetSearchType, setTimeSheetSearchType] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [status, setStatus] = useState('1');
  const [isTimeSheetImported, setIsTimeSheetImported] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [tempSearch, setTempSearch] = useState('');
  const [tempSearchType, setTempSearchType] = useState('');
  const [tempDateRange, setTempDateRange] = useState([null, null]);
  const [tempIsImported, setTempIsImported] = useState(false);

  const [startDate, endDate] = dateRange;
  const [tempStartDate, tempEndDate] = tempDateRange;

  const { timeSheetVerified } = useImportedTimeSheet();
  const token = localStorage.getItem('xsrfToken');

  const openFilterSheet = () => {
    setTempSearch(timeSheetSearch);
    setTempSearchType(timeSheetSearchType);
    setTempDateRange(dateRange);
    setTempIsImported(isTimeSheetImported);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setTimeSheetSearch(tempSearch);
    setTimeSheetSearchType(tempSearchType);
    setDateRange(tempDateRange);
    setIsTimeSheetImported(tempIsImported);
    setIsFilterOpen(false);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [timeSheetSearch, timeSheetSearchType, startDate, endDate, status, isTimeSheetImported]);

  useEffect(() => {
    setIsWaiting(true);
    ApiDatabase.getTimeSheets(
      {
        token,
        status,
        searchOptions: {
          type: null,
          startDate: startDate ? formatDateToYMD(startDate) : null,
          endDate: endDate ? formatDateToYMD(endDate) : null,
          isTimeSheetImported,
        },
      },
      (data) => {
        setArrayTimeSheets(data.timeSheets);
        setCountPerPage(limitElement);
        setIsWaiting(false);
      },
      (err) => { if (err.name !== 'CanceledError') console.log(err); }
    );
  }, [companyData, refreshTime, startDate, endDate, status, isTimeSheetImported]);

  const fuse = useMemo(() => new Fuse(arrayTimeSheets, FUSE_OPTIONS), [arrayTimeSheets]);

  const { timeSheetsToDisplay, countTotal, totalPages } = useMemo(() => {
    let filtered = arrayTimeSheets;

    if (timeSheetSearch.length > 0) {
      const searchTerm = removeAccents(timeSheetSearch.toLowerCase());

      if (timeSheetSearchType) {
        filtered = arrayTimeSheets.filter((item) => {
          const searchFields = {
            id_time_sheet: item.id_time_sheet?.toString(),
            id_mission: item.id_mission_reel?.toString(),
            id_contract_bestt: item.id_bestt_contract?.toString(),
            id_bestt_temp_worker: item.id_bestt_temp_worker?.toString(),
            name_company: removeAccents((item.reason_social + ' ' + item.name_commercial)?.toLowerCase()),
            temp_worker_name: removeAccents((item.firstname + ' ' + item.lastname)?.toLowerCase()),
          };
          return (searchFields[timeSheetSearchType] || '').includes(searchTerm);
        });
      } else {
        const searchTerms = searchTerm.split(' ');
        const combinedResults = {};

        searchTerms.forEach((term) => {
          fuse.search(term).forEach(({ item, score }) => {
            const key = item._id + item.id_mission;
            if (combinedResults[key]) combinedResults[key].score -= score;
            else combinedResults[key] = { item, score };
          });
        });

        filtered = Object.values(combinedResults)
          .sort((a, b) => a.score - b.score)
          .map((r) => r.item);
      }
    }

    const total = filtered.length;
    const pages = Math.ceil(total / countPerPage) || 1;
    const sliced = filtered.slice((currentPage - 1) * countPerPage, currentPage * countPerPage);
    return { timeSheetsToDisplay: sliced, countTotal: total, totalPages: pages };
  }, [timeSheetSearch, timeSheetSearchType, arrayTimeSheets, currentPage, countPerPage, fuse]);

  if (location.pathname === '/timeSheet') {
    const lastTab = sessionStorage.getItem('lastTimeSheetTab') || 'toBeCompleted';
    return <Navigate to={`/timeSheet/${lastTab}`} replace />;
  }

  return (
    <PageForm>
      <>
        {!location.pathname.startsWith('/timeSheet/ficheHour/') && (
          <SubNavbar>
            <div className="flex justify-between items-center w-full gap-3">
              <MediaQuery minWidth={768}>
                <div className="flex-1 overflow-x-auto overflow-y-hidden scrollbar-hide">
                  <div className="flex gap-3 items-center min-w-max">
                    <div className="text-sm relative z-30">
                      <DatePicker
                        selectsRange
                        startDate={startDate}
                        endDate={endDate}
                        onChange={setDateRange}
                        isClearable
                        placeholderText="Sélectionner une période"
                        className="w-full min-w-[180px] h-10 pl-3 pr-8 ft-b border rounded-md text-left text-sm focus:border-yellow-500 focus:outline-none"
                        locale="fr-FR"
                        dateFormat="dd/MM/yyyy"
                        popperPlacement="bottom-start"
                        popperProps={{ strategy: 'fixed' }}
                      />
                    </div>
                    <div className="flex gap-2 items-center">
                      <div className="flex-1 max-w-[280px] text-sm searchbar-time-sheet">
                        <StyledInput
                          type="text"
                          placeholder="Rechercher"
                          imgFront={<SearchLoop wh={16} color="#6B7280" />}
                          value={timeSheetSearch}
                          onChange={(e) => setTimeSheetSearch(e.target.value)}
                        />
                      </div>
                      <div className="max-w-[120px]">
                        <SearchTypeSelect
                          value={timeSheetSearchType}
                          onChange={setTimeSheetSearchType}
                          className="options-time-sheet"
                        />
                      </div>
                      {timeSheetVerified && (
                        <div className="border rounded py-2 px-4 text-sm imported-time-sheet">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <ThreeStateCheckbox checked={isTimeSheetImported} onChange={setIsTimeSheetImported} />
                            <div>Importé</div>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </MediaQuery>

              <MediaQuery maxWidth={767}>
                <div className="flex items-center gap-3 min-w-max">
                  <Button color="SECONDARY" size="SM" onClick={openFilterSheet}>
                    <Filter wh={16} color="#374151" />
                    <span>Filtrer</span>
                  </Button>
                  <div className="h-8 w-px bg-gray-200" />
                  <TimeSheetSubNav />
                </div>
              </MediaQuery>

              <MediaQuery minWidth={768}>
                <div className="flex-shrink-0 flex items-center gap-3">
                  <div className="h-8 w-px bg-gray-200" />
                  <TimeSheetSubNav />
                </div>
              </MediaQuery>
            </div>
          </SubNavbar>
        )}

        <div className="flex flex-col w-full h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <Outlet
              context={{
                timeSheetsToDisplay,
                currentPage,
                setCurrentPage,
                totalPages,
                countPerPage,
                countTotal,
                isWaiting,
                setStatus,
              }}
            />
          </div>
        </div>

        <BottomSheet isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filtrer">
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
              <DatePicker
                selectsRange
                startDate={tempStartDate}
                endDate={tempEndDate}
                onChange={setTempDateRange}
                isClearable
                placeholderText="Sélectionner une période"
                className="w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-left text-sm focus:border-yellow-500 focus:outline-none"
                locale="fr-FR"
                dateFormat="dd/MM/yyyy"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher</label>
              <StyledInput
                type="text"
                placeholder="Rechercher..."
                imgFront={<SearchLoop wh={16} color="#6B7280" />}
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher par</label>
              <SearchTypeSelect
                value={tempSearchType}
                onChange={setTempSearchType}
                className="rounded-lg"
              />
            </div>
            {timeSheetVerified && (
              <div className="border rounded-lg py-3 px-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <ThreeStateCheckbox checked={tempIsImported} onChange={setTempIsImported} />
                  <span className="text-sm">Relevés importés</span>
                </label>
              </div>
            )}
            <div className="mt-2">
              <Button color="PRIMARY" size="FULL" onClick={applyFilters}>Appliquer</Button>
            </div>
          </div>
        </BottomSheet>
      </>
    </PageForm>
  );
};

export default TimeSheet;
